import { ActionIcon, Affix, Card, Tooltip } from '@mantine/core';
import { IconUsersGroup } from '@tabler/icons-react';
import { QueryObserverResult } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Node, useNodesState } from 'reactflow';
import { Show } from '../../../../../react-commons/components/control-flow/Show';
import { OpenModal } from '../../../../../react-commons/utils/modals/openModal';
import { VCycleElementType } from '../../../resource/enumerations/VCycleElementType';
import { VCycleSide } from '../../../resource/enumerations/VCycleSide';
import {
    StructuredVCycleContainerOutline,
    StructuredVCycleElementOutline,
    StructuredVCycleObjectOutline,
} from '../../../resource/outlines/vcycleElementOutline';
import { useProjectVersionPublishmentActions } from '../../../resource/resources/artifacts/project/projectVersionPublishmentResource';
import { useProjectVersionActions } from '../../../resource/resources/artifacts/project/projectVersionResource';
import { useVCycleElementContainerActions } from '../../../resource/resources/artifacts/use_case/representation/vcycleElementContainerResource';
import { useWorkflowRepresentationActions } from '../../../resource/resources/artifacts/use_case/representation/workflowRepresentationResource';
import {
    useVCycleElementContainerFilterActions,
    VCycleElementContainerFilterResult,
} from '../../../resource/resources/persistables/user/vcycleElementContainerFilterResource';
import { LinkCopyButtonComponent } from '../../reusable-components/LinkCopyButtonComponent';
import { MyNavigate, SomeRouteInfo, useMyNavigate } from '../../router/createdRouter';
import { VShapeFlowProvider } from './vcycle-shape/VShapeFlowProvider';
import { AssociatedRolesInWorkflowComponent } from './vcycle-viewer/AssociatedRolesInWorkflowComponent';
import { FilterBarComponent } from './vcycle-viewer/FilterBarComponent';

class NodeTooSmallException extends Error {
    constructor() {
        super('');
    }
}

const getWindowDimensions = () => {
    const { innerWidth: width, innerHeight: height } = window;
    return {
        width,
        height,
    };
};

export const parseNodes = (
    width: number,
    height: number,
    query: QueryObserverResult<StructuredVCycleObjectOutline | null | undefined>,
    navigate: MyNavigate,
    projectId: string,
    projectVersionId: string,
    publishmentTitle: string | undefined,
    standardId: string | undefined,
    useCaseId: string | undefined,
    useCaseObjectId: string | undefined,
    workflowId: string,
    workflowObjectId: string | undefined,
    appliedFilter: VCycleElementContainerFilterResult | null,
    withLargeNodes: boolean
) => {
    const data = query.data;
    if (!data?.containers?.length) {
        return [];
    }

    const doRowsClash = (affectedRowsOne: Array<number>, affectedRowsTwo: Array<number>) => {
        return affectedRowsOne.find((row) => affectedRowsTwo.includes(row)) !== undefined;
    };

    const elementMatchedInFilter = (containerId: string, elementId: string) => {
        if (appliedFilter === null) {
            return true;
        }
        const found =
            appliedFilter.processAreaContainerResults
                .find((value) => value.id === containerId)
                ?.matchingVCycleElements?.find((value) => value === elementId) !== undefined;

        if (found) {
            return true;
        } else {
            console.log('TODO: Embedded Vcycle');
            return found;
        }
    };

    const getContainerIndexOccurrence = (
        array: Array<StructuredVCycleContainerOutline>,
        element: StructuredVCycleElementOutline
    ) => {
        let maxCalculatedHorizontalPriority = 0;
        array.forEach((container) => {
            container.elements
                .filter((value) => doRowsClash(value.affectsRows, element.affectsRows))
                .forEach((innerElement) => {
                    const calculatedHorizontalPriority = getCalculatedHorizontalPriority(
                        array,
                        container,
                        innerElement
                    );
                    if (calculatedHorizontalPriority > maxCalculatedHorizontalPriority) {
                        maxCalculatedHorizontalPriority = calculatedHorizontalPriority;
                    }
                });
        });
        return maxCalculatedHorizontalPriority;
    };

    const getIndexOccurrence = (
        array: Array<StructuredVCycleContainerOutline>,
        element: StructuredVCycleElementOutline
    ) => {
        let maxCalculatedHorizontalPriority = 0;
        let toReduceBy = 0;
        array.forEach((container) => {
            container.elements
                .filter((value) => doRowsClash(value.affectsRows, element.affectsRows))
                .forEach((innerElement) => {
                    const calculatedHorizontalPrio = getCalculatedHorizontalPriority(array, container, innerElement);
                    if (innerElement === element) {
                        const elementsOnSameVerticality = container.elements.filter(
                            (value) => value.verticalPriority === element.verticalPriority
                        );
                        const biggestHorizontalPriorityInContainer = Math.max(
                            ...container.elements.map((value) => value.horizontalPriority)
                        );
                        toReduceBy = biggestHorizontalPriorityInContainer - elementsOnSameVerticality.length;
                    }
                    if (calculatedHorizontalPrio > maxCalculatedHorizontalPriority) {
                        maxCalculatedHorizontalPriority = calculatedHorizontalPrio;
                    }
                });
        });
        return maxCalculatedHorizontalPriority - toReduceBy;
    };

    const getMaxVerticalPriority = (array: Array<StructuredVCycleElementOutline>) => {
        return Math.max(...array.map((value) => value.verticalPriority));
    };

    const getRowSpanPriority = (
        array: Array<StructuredVCycleContainerOutline>,
        container: StructuredVCycleContainerOutline,
        affectsRows?: Array<number>
    ) => {
        const affectsRowsToUse = affectsRows ?? container?.affectsRows ?? [];
        const affectedContainers = array.filter(
            (value) => affectsRowsToUse.find((n) => value.affectsRows.includes(n)) !== undefined
        );
        const sorted = affectedContainers.toSorted((a, b) => a.index - b.index);
        let additionalPriority = 0;
        if (sorted[0] !== container) {
            additionalPriority = getRowSpanPriority(array, sorted[0]);
        }
        return sorted.indexOf(container) + additionalPriority;
    };

    const getCalculatedHorizontalPriority = (
        array: Array<StructuredVCycleContainerOutline>,
        container: StructuredVCycleContainerOutline,
        element: StructuredVCycleElementOutline
    ) => {
        const affectedContainers = array
            .filter((value) => doRowsClash(value.affectsRows, element.affectsRows))
            .filter((value) => value.column < container.column);

        const maxHorizontalPriorities: Array<number> = [];
        element.affectsRows.forEach((affectedRow) => {
            let horizontalPrioSum = 0;
            affectedContainers
                .filter((value) => value.affectsRows.includes(affectedRow))
                .forEach((affectedContainer) => {
                    let biggestHorizontalPrio = 0;
                    affectedContainer.elements
                        .filter((value) => value.affectsRows.includes(affectedRow))
                        .forEach((affectedElement) => {
                            if (affectedElement.horizontalPriority > biggestHorizontalPrio) {
                                biggestHorizontalPrio = affectedElement.horizontalPriority;
                            }
                        });
                    horizontalPrioSum += biggestHorizontalPrio;
                });
            maxHorizontalPriorities.push(horizontalPrioSum);
        });

        return element.horizontalPriority + Math.max(...maxHorizontalPriorities);
    };

    const leftElements = data.containers.filter((value) => value.side === VCycleSide.LEFT);
    const rightElements = data.containers.filter((value) => value.side === VCycleSide.RIGHT);

    const leftLastIndex = Math.max(...leftElements.flatMap((value) => value.affectsRows));
    const rightLastIndex = Math.max(...rightElements.flatMap((value) => value.affectsRows));

    const totalLastIndex = Math.max(leftLastIndex, rightLastIndex);

    let maxIndexOccurrence = data.maxNumberOfColumns;
    leftElements.forEach((container) => {
        const rowSpanPriority = getRowSpanPriority(leftElements, container) + 1;
        if (rowSpanPriority > maxIndexOccurrence) {
            maxIndexOccurrence = rowSpanPriority;
        }
    });
    rightElements.forEach((container) => {
        const rowSpanPriority = getRowSpanPriority(rightElements, container) + 1;
        if (rowSpanPriority > maxIndexOccurrence) {
            maxIndexOccurrence = rowSpanPriority;
        }
    });

    if (maxIndexOccurrence < 3 && withLargeNodes) {
        maxIndexOccurrence = 3;
    }

    const leftBaseX = 0;
    const leftBaseY = 15 * data.maxNumberOfRows;

    const rightBaseX = 0;
    const rightBaseY = 15 * data.maxNumberOfRows;

    const leftWingNodes: Array<Node> = [];

    leftElements
        .toSorted((a, b) => b.index - a.index)
        .forEach((container) => {
            container.elements
                .toSorted((a, b) => a.horizontalPriority - b.horizontalPriority)
                .forEach((element) => {
                    const startingIndex = Math.min(...element.affectsRows);
                    const indexOccurrence = getIndexOccurrence(leftElements, element);
                    const containerIndexOccurrence = getContainerIndexOccurrence(leftElements, element);
                    const maxVerticalPriority = getMaxVerticalPriority(container.elements);

                    const calculatedHorizontalPriority = getCalculatedHorizontalPriority(
                        leftElements,
                        container,
                        element
                    );

                    const scale =
                        maxIndexOccurrence < 3 ?
                            indexOccurrence > 1 ?
                                1.45
                            :   3
                        : (
                            containerIndexOccurrence > indexOccurrence &&
                            leftElements.find(
                                (value) => value !== container && doRowsClash(element.affectsRows, value.affectsRows)
                            ) !== undefined
                        ) ?
                            containerIndexOccurrence > 4 ? 1.967
                            : containerIndexOccurrence === 4 ? 3
                            : containerIndexOccurrence === 3 ? 4.05
                            : containerIndexOccurrence === 2 ? 6.1
                            : 3
                        : indexOccurrence > 3 ? 1.45
                        : indexOccurrence === 3 ? 1.967
                        : indexOccurrence === 2 ? 3
                        : 6.1;

                    const nodeHeight =
                        ((80 - (maxVerticalPriority > 1 ? 6 : 0)) * (container.rowSpan ?? 1)) / maxVerticalPriority +
                        6.5 * (container.rowSpan - 1);

                    const xRelativeToHeightMultiplier = Math.round(nodeHeight / 42);

                    const nodeBaseWidth =
                        scale === 1.45 ? 109
                        : scale === 1.967 ? 150
                        : scale === 4.05 ? 310
                        : scale === 6.1 ? 471
                        : 230;

                    const nodeWidth = nodeBaseWidth + xRelativeToHeightMultiplier * 25;

                    const horizontalOffsetAddition =
                        xRelativeToHeightMultiplier === 1 ? 13
                        : xRelativeToHeightMultiplier === 2 ? 39
                        : xRelativeToHeightMultiplier === 3 ? 78
                        : xRelativeToHeightMultiplier === 4 ? 88
                        : 144;

                    const horizontalOffSet =
                        indexOccurrence > 1 && calculatedHorizontalPriority !== 1 ?
                            (nodeWidth - horizontalOffsetAddition) * (calculatedHorizontalPriority - 1)
                        :   0;

                    const horOffsetBasedOffVertPrio =
                        (
                            container.elements.filter(
                                (value) =>
                                    value !== element &&
                                    value.affectsRows.find((row) => element.affectsRows.includes(row)) !== undefined
                            ).length
                        ) ?
                            (element.verticalPriority - 1) * 24
                        :   0;

                    const positionX =
                        width / 2 -
                        (leftBaseX + (totalLastIndex - Math.max(...element.affectsRows)) * 47) -
                        horizontalOffSet -
                        horOffsetBasedOffVertPrio -
                        nodeWidth;

                    const xCenter =
                        (scale === 1.45 ? 38
                        : scale === 4.05 ? 38
                        : scale === 1.967 ? 38
                        : scale === 6.1 ? 38
                        : 38) + Math.pow(1.2, xRelativeToHeightMultiplier - 1);

                    const verticalPriorityOffsetMultiplier = (xRelativeToHeightMultiplier - 1) * 1.5 + 42;
                    const verticalPriorityOffset =
                        container.affectsRows.length === element.affectsRows.length ?
                            (maxVerticalPriority - element.verticalPriority) * verticalPriorityOffsetMultiplier
                        :   0;

                    const positionY =
                        height / 2 +
                        (leftBaseY + 85 + verticalPriorityOffset - (totalLastIndex - startingIndex) * 85) -
                        (xRelativeToHeightMultiplier - 1) * 0.5;

                    const routeInfo = (
                        element.type === VCycleElementType.REPRESENTATION ?
                            publishmentTitle ?
                                {
                                    route: '/project/:id/published-use-case/:useCaseObjectId/published-workflow-representation/:workflowObjectId',
                                    routeParams: {
                                        id: projectId,
                                        useCaseObjectId: useCaseObjectId ?? '',
                                        workflowObjectId: element.referencedObjectId,
                                    },
                                    searchParams: { publishment: publishmentTitle },
                                }
                            :   {
                                    route: '/project/:id/active-version/:versionId/standard/:standardId/use-case/:useCaseId/workflow-representations',
                                    routeParams: {
                                        id: projectId,
                                        versionId: projectVersionId,
                                        standardId: standardId ?? '',
                                        useCaseId: useCaseId ?? '',
                                    },
                                    searchParams: { selected: element.referencedId },
                                }
                        : publishmentTitle ?
                            {
                                route: '/project/:id/published-process-area/:processAreaObjectId/published-use-case/:useCaseObjectId/published-workflow-representation/:workflowObjectId',
                                routeParams: {
                                    id: projectId,
                                    processAreaObjectId: element.referencedObjectId,
                                    useCaseObjectId: useCaseObjectId ?? '',
                                    workflowObjectId: workflowObjectId ?? '',
                                },
                                searchParams: { publishment: publishmentTitle },
                            }
                        :   {
                                route: '/project/:id/active-version/:versionId/process-area/:processAreaId/standard/:standardId/use-case/:useCaseId/workflow-representation/:workflowId',
                                routeParams: {
                                    id: projectId,
                                    versionId: projectVersionId,
                                    processAreaId: element.referencedId,
                                    standardId: standardId ?? '',
                                    useCaseId: useCaseId ?? '',
                                    workflowId: workflowId,
                                },
                                searchParams: {},
                            }) satisfies SomeRouteInfo;

                    let colorShade = 8 - container.embeddedDepth * 2;
                    if (colorShade < 0) {
                        colorShade = 0;
                    }

                    const textWidth = Math.max(nodeWidth - 80, element.displayName.length * 8);
                    const numberOfLines = Math.ceil(textWidth / (nodeWidth - 80));

                    if (numberOfLines * 20 > nodeHeight && !withLargeNodes) {
                        throw new NodeTooSmallException();
                    }

                    leftWingNodes.push({
                        id: element.id,
                        data: {
                            label: element.displayName,
                            gradientStart: element.backgroundColor ?? `var(--mantine-primary-color-${colorShade})`,
                            gradientEnd: element.backgroundColor ?? `var(--mantine-primary-color-${colorShade})`,
                            textColor: element.textColor,
                            height: nodeHeight,
                            width: nodeWidth,
                            scale,
                            xCenter,
                            onClick: () => navigate(routeInfo),
                            tooltipDescription: element.description ? element.description : undefined,
                            maxOccurrence: maxIndexOccurrence,
                            occurrences: indexOccurrence,
                            disabled: !elementMatchedInFilter(container.id, element.id),
                        },
                        position: { x: positionX, y: positionY },
                        style: {
                            width: 'auto',
                            minWidth: nodeWidth,
                            minHeight: nodeHeight,
                            maxWidth: nodeWidth,
                            maxHeight: nodeHeight,
                        },
                        deletable: false,
                        type: 'leftWing',
                        zIndex: 1,
                    });
                });
        });

    const rightWingNodes: Array<Node> = [];

    rightElements.forEach((container) => {
        container.elements
            .toSorted((a, b) => a.horizontalPriority - b.horizontalPriority)
            .forEach((element) => {
                const startingIndex = Math.min(...element.affectsRows);
                const indexOccurrence = getIndexOccurrence(rightElements, element);
                const containerIndexOccurrence = getContainerIndexOccurrence(rightElements, element);
                const maxVerticalPriority = getMaxVerticalPriority(container.elements);

                const calculatedHorizontalPriority = getCalculatedHorizontalPriority(rightElements, container, element);

                const scale =
                    maxIndexOccurrence < 3 ?
                        indexOccurrence > 1 ?
                            1.45
                        :   3
                    : (
                        containerIndexOccurrence > indexOccurrence &&
                        rightElements.find(
                            (value) => value !== container && doRowsClash(element.affectsRows, value.affectsRows)
                        ) !== undefined
                    ) ?
                        containerIndexOccurrence > 4 ? 1.967
                        : containerIndexOccurrence === 4 ? 3
                        : containerIndexOccurrence === 3 ? 4.05
                        : containerIndexOccurrence === 2 ? 6.1
                        : 3
                    : indexOccurrence > 3 ? 1.45
                    : indexOccurrence === 3 ? 1.967
                    : indexOccurrence === 2 ? 3
                    : 6.1;

                const nodeHeight =
                    ((80 - (maxVerticalPriority > 1 ? 6 : 0)) * (container.rowSpan ?? 1)) / maxVerticalPriority +
                    6.5 * (container.rowSpan - 1);

                const xRelativeToHeightMultiplier = Math.round(nodeHeight / 42);

                const nodeBaseWidth =
                    scale === 1.45 ? 109
                    : scale === 1.967 ? 150
                    : scale === 4.05 ? 310
                    : scale === 6.1 ? 471
                    : 230;

                const nodeWidth = nodeBaseWidth + xRelativeToHeightMultiplier * 25;

                const horizontalOffsetAddition =
                    xRelativeToHeightMultiplier === 1 ? 13
                    : xRelativeToHeightMultiplier === 2 ? 38
                    : xRelativeToHeightMultiplier === 3 ? 78
                    : xRelativeToHeightMultiplier === 4 ? 88
                    : 184;

                const horizontalOffSet =
                    indexOccurrence > 1 && calculatedHorizontalPriority !== 1 ?
                        (nodeWidth - horizontalOffsetAddition) * (calculatedHorizontalPriority - 1)
                    :   0;

                const horOffsetBasedOffVertPrio =
                    (
                        container.elements.filter(
                            (value) =>
                                value !== element &&
                                value.affectsRows.find((row) => element.affectsRows.includes(row)) !== undefined
                        ).length
                    ) ?
                        (element.verticalPriority - 1) * 24
                    :   0;

                const positionX =
                    width / 2 +
                    (rightBaseX + (totalLastIndex - Math.max(...element.affectsRows)) * 47) +
                    horizontalOffSet +
                    horOffsetBasedOffVertPrio;

                const xCenter =
                    scale === 1.45 ? 36 + 17 * xRelativeToHeightMultiplier
                    : scale === 4.05 ? 38 + 6 * xRelativeToHeightMultiplier
                    : scale === 1.967 ? 38 + 12 * xRelativeToHeightMultiplier
                    : scale === 6.1 ? 38.5 + 4 * xRelativeToHeightMultiplier
                    : 38 + 8 * xRelativeToHeightMultiplier;

                const verticalPriorityOffsetMultiplier = (xRelativeToHeightMultiplier - 1) * 1.5 + 42;
                const verticalPriorityOffset =
                    container.affectsRows.length === element.affectsRows.length ?
                        (maxVerticalPriority - element.verticalPriority) * verticalPriorityOffsetMultiplier
                    :   0;

                const positionY =
                    height / 2 +
                    (rightBaseY + 85 + verticalPriorityOffset - (totalLastIndex - startingIndex) * 85) -
                    (xRelativeToHeightMultiplier - 1) * 0.5;

                const routeInfo = (
                    element.type === VCycleElementType.REPRESENTATION ?
                        publishmentTitle ?
                            {
                                route: '/project/:id/published-use-case/:useCaseObjectId/published-workflow-representation/:workflowObjectId',
                                routeParams: {
                                    id: projectId,
                                    useCaseObjectId: useCaseObjectId ?? '',
                                    workflowObjectId: element.referencedObjectId,
                                },
                                searchParams: { publishment: publishmentTitle },
                            }
                        :   {
                                route: '/project/:id/active-version/:versionId/standard/:standardId/use-case/:useCaseId/workflow-representations',
                                routeParams: {
                                    id: projectId,
                                    versionId: projectVersionId,
                                    standardId: standardId ?? '',
                                    useCaseId: useCaseId ?? '',
                                },
                                searchParams: { selected: element.referencedId },
                            }
                    : publishmentTitle ?
                        {
                            route: '/project/:id/published-process-area/:processAreaObjectId/published-use-case/:useCaseObjectId/published-workflow-representation/:workflowObjectId',
                            routeParams: {
                                id: projectId,
                                processAreaObjectId: element.referencedObjectId,
                                useCaseObjectId: useCaseObjectId ?? '',
                                workflowObjectId: workflowObjectId ?? '',
                            },
                            searchParams: { publishment: publishmentTitle },
                        }
                    :   {
                            route: '/project/:id/active-version/:versionId/process-area/:processAreaId/standard/:standardId/use-case/:useCaseId/workflow-representation/:workflowId',
                            routeParams: {
                                id: projectId,
                                versionId: projectVersionId,
                                processAreaId: element.referencedId,
                                standardId: standardId ?? '',
                                useCaseId: useCaseId ?? '',
                                workflowId: workflowId,
                            },
                            searchParams: {},
                        }) satisfies SomeRouteInfo;

                let colorShade = 8 - container.embeddedDepth * 2;
                if (colorShade < 0) {
                    colorShade = 0;
                }

                const textWidth = Math.max(nodeWidth - 80, element.displayName.length * 8);
                const numberOfLines = Math.ceil(textWidth / (nodeWidth - 80));
                if (numberOfLines * 20 > nodeHeight && !withLargeNodes) {
                    throw new NodeTooSmallException();
                }

                rightWingNodes.push({
                    id: element.id,
                    data: {
                        label: element.displayName,
                        gradientStart: element.backgroundColor ?? `var(--mantine-primary-color-${colorShade})`,
                        gradientEnd: element.backgroundColor ?? `var(--mantine-primary-color-${colorShade})`,
                        textColor: element.textColor,
                        height: nodeHeight,
                        width: nodeWidth,
                        scale,
                        xCenter,
                        onClick: () => navigate(routeInfo),
                        tooltipDescription: element.description ? element.description : undefined,
                        maxOccurrence: maxIndexOccurrence,
                        occurrences: indexOccurrence,
                        disabled: !elementMatchedInFilter(container.id, element.id),
                    },
                    position: { x: positionX, y: positionY },
                    style: {
                        width: 'auto',
                        minWidth: nodeWidth,
                        minHeight: nodeHeight,
                        maxWidth: nodeWidth,
                        maxHeight: nodeHeight,
                    },
                    deletable: false,
                    type: 'rightWing',
                    zIndex: 1,
                });
            });
    });

    // const processedRightIndexes: number[] = []

    return leftWingNodes.concat(rightWingNodes);
};

export const VCycleRepresentationViewerComponent = ({
    inTabs,
    workflowId,
    useCaseObjectId,
}: {
    inTabs: number | undefined;
    workflowId: string;
    useCaseObjectId: string;
}) => {
    const {
        id: projectId,
        versionId,
        standardId,
        useCaseId,
        workflowObjectId,
    } = useParams<{
        id: string;
        versionId: string;
        standardId: string;
        useCaseId: string;
        workflowObjectId: string;
    }>();
    const [search] = useSearchParams();
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());
    const [nodes, setNodes] = useNodesState(Array<Node>());
    const [appliedFilter, setAppliedFilter] = React.useState<VCycleElementContainerFilterResult | null>(null);

    const navigate = useMyNavigate();
    const projectVersionActions = useProjectVersionActions();
    const vcycleContainerFilterActions = useVCycleElementContainerFilterActions();
    const projectVersionPublishmentActions = useProjectVersionPublishmentActions();
    const workflowActions = useWorkflowRepresentationActions();
    const vcycleElementActions = useVCycleElementContainerActions();
    const projectVersionQuery = projectVersionActions.useGetByPublishment(search.get('publishment'), projectId);
    const projectVersionPublishmentQuery = projectVersionPublishmentActions.useGetSingleResource(
        versionId ?? projectVersionQuery.data?.id
    );
    const workflowByIdQuery = workflowActions.useGetResource(workflowId);
    const vcycleElementsQuery = vcycleElementActions.useGetStructuredVCycleElementContainers(workflowId);
    const applyFilterFunction = vcycleContainerFilterActions.useApplyVCycleFilter();

    const link =
        location.href.substring(0, location.href.indexOf('project')) +
        `project/${projectId}/published-use-case/${useCaseObjectId}/published-workflow-representation/${workflowByIdQuery.data?.objectId}?publishment=${encodeURIComponent(search.get('publishment') ?? projectVersionPublishmentQuery.data?.title ?? '')}`;

    const triggerParseNodes = () => {
        let newNodes = Array<Node>();
        try {
            newNodes = parseNodes(
                windowDimensions.width,
                windowDimensions.height - 60,
                vcycleElementsQuery,
                navigate,
                projectId ?? '',
                versionId ?? '',
                search.get('publishment') ?? undefined,
                standardId,
                useCaseId,
                useCaseObjectId,
                workflowId,
                workflowObjectId,
                appliedFilter,
                false
            );
        } catch (e) {
            newNodes = parseNodes(
                windowDimensions.width,
                windowDimensions.height - 60,
                vcycleElementsQuery,
                navigate,
                projectId ?? '',
                versionId ?? '',
                search.get('publishment') ?? undefined,
                standardId,
                useCaseId,
                useCaseObjectId,
                workflowId,
                workflowObjectId,
                appliedFilter,
                true
            );
        } finally {
            setNodes(newNodes ?? []);
        }
    };

    useEffect(() => {
        function handleResize() {
            setWindowDimensions(getWindowDimensions());
        }

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        triggerParseNodes();
    }, [vcycleElementsQuery.data, appliedFilter]);

    return (
        <Card style={{ width: windowDimensions.width, height: windowDimensions.height - 60 }}>
            <Show when={projectVersionPublishmentQuery.data && inTabs !== undefined}>
                <Affix position={{ top: inTabs, right: 50 }}>
                    <Tooltip label={'Copy Link'}>
                        <LinkCopyButtonComponent link={link} variant={'default'} />
                    </Tooltip>
                </Affix>
            </Show>
            <Show when={inTabs !== undefined}>
                <Affix position={{ top: inTabs, left: 15 }}>
                    <FilterBarComponent
                        onApplyFilter={(value) => {
                            if (!value) {
                                setAppliedFilter(null);
                            } else {
                                applyFilterFunction.mutate(
                                    {
                                        id: workflowId,
                                        filterResource: value,
                                    },
                                    {
                                        onSuccess: (data) => {
                                            setAppliedFilter(data);
                                        },
                                    }
                                );
                            }
                        }}
                    />
                </Affix>
            </Show>
            <Show when={inTabs !== undefined}>
                <Affix position={{ top: inTabs, right: 15 }}>
                    <Tooltip label={'View Associated Roles within V-Cycle'}>
                        <ActionIcon
                            onClick={(_) => {
                                OpenModal.ContentModal({
                                    title: 'All Associated Roles',
                                    content: (
                                        <AssociatedRolesInWorkflowComponent
                                            projectVersionId={versionId}
                                            workflowRepresentationId={workflowId}
                                        />
                                    ),
                                });
                            }}
                            variant={'default'}
                            color={'var(--mantine-color-base-button)'}
                        >
                            <IconUsersGroup />
                        </ActionIcon>
                    </Tooltip>
                </Affix>
            </Show>
            <Card.Section>
                <div style={{ width: windowDimensions.width, height: windowDimensions.height - 60, margin: 'auto' }}>
                    <VShapeFlowProvider
                        width={windowDimensions.width}
                        height={windowDimensions.height - 60}
                        nodes={nodes}
                    />
                </div>
            </Card.Section>
        </Card>
    );
};
